import React, { useCallback, useEffect, useState } from "react";
import { COLOR_CODES } from "../../common";
import { BuyerSellerResponse } from "../../models/buyerSeller";
import { getTopBuyerSellerData } from "../../Rest_api/RestApi";
// import { fetchData } from "./../Rajasthan/Auction/fetchData";

interface TopBuyerSellerTableProps {
  startDate: string;
  endDate: string;
}

export function TopBuyerSellerTable({
  startDate,
  endDate,
}: TopBuyerSellerTableProps) {
  const [data, setData] = useState<BuyerSellerResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Convert date format from YYYY-MM-DD to DD-MM-YYYY
      const formatDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split("-");
        return `${day}-${month}-${year}`;
      };

      const response = await getTopBuyerSellerData({
        exchange: "IEX",
        product: "DAM",
        region: "NRLDC,WRLDC,SRLDC,ERLDC,NERLDC",
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      });

      // Sort buyers and sellers by value (descending) and take top 10
      const sortedBuyers = response.buyer
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      const sortedSellers = response.seller
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      setData({
        buyer: sortedBuyers,
        seller: sortedSellers,
      });
    } catch (error) {
      console.error("Error fetching top buyer seller data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [endDate, startDate]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, fetchData]);

  const formatValue = (value: number) => {
    return (value / 1000).toFixed(1) + " MU"; // Convert to Million Units
  };

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
          <p className="text-gray-700 text-sm font-medium">
            Loading participant data...
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <p className="text-lg font-medium">No data available</p>
          <p className="text-sm mt-2">Unable to load participant data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Headers */}
      <div className="flex gap-4 mb-2">
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-600 text-center">
            Top 10 Buyers (by Volume)
          </h4>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-600 text-center">
            Top 10 Sellers (by Volume)
          </h4>
        </div>
      </div>
      
      {/* Single scrollable container for both tables */}
      <div className="flex-1 flex gap-4 min-h-0 overflow-y-auto">
        {/* Top 10 Buyers */}
        <div className="flex-1">
          <div className="space-y-1.5">
            {data.buyer.map((buyer, index) => (
              <div
                key={index}
                className="flex justify-between items-center py-1 px-2 bg-white rounded shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="text-xs font-normal text-gray-600 flex-1 truncate mr-2">
                  {buyer.name.replace(/_/g, ' ')}
                </span>
                <span
                  className="text-xs font-medium flex-shrink-0"
                  style={{ color: COLOR_CODES.primary }}
                >
                  {formatValue(buyer.value)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Top 10 Sellers */}
        <div className="flex-1">
          <div className="space-y-1.5">
            {data.seller.map((seller, index) => (
              <div
                key={index}
                className="flex justify-between items-center py-1 px-2 bg-white rounded shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="text-xs font-normal text-gray-600 flex-1 truncate mr-2">
                  {seller.name.replace(/_/g, ' ')}
                </span>
                <span
                  className="text-xs font-medium flex-shrink-0"
                  style={{ color: COLOR_CODES.secondary }}
                >
                  {formatValue(seller.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
